import React, { useState } from 'react'
import { Input, IconButton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shopflo/ui'
import { X, CaretDown, Check, Trash } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import { useEntitledProducts } from '../../../hooks/useEntitledProducts'
import {
  DiscountApplyToType,
  AmountEntitlement,
  EntitledCatalogueSelectionMode,
  DiscountFieldPaths
} from 'src/lib/types/discountsV2'
import CatalogueGroupControl from '../common/CatalogueGroupControl'
import { classNames } from 'src/lib/utils/helpers'
import FormErrorText from 'src/components/common/FormErrorText'
import { useLocale } from 'src/lib/hooks/useLocal'
import { discountCatalogueErrors } from 'src/lib/utils/catalogueErrorHelpers'

const EntitledProductsList = () => {
  const {
    state: { entitlement: rawEntitlement, errors },
    actions: { setFormState }
  } = useDiscountForm()

  const entitlement = rawEntitlement as AmountEntitlement
  const { entitledCatalogue, metaData } = entitlement
  const { applyTo } = entitlement
  const items = metaData?.entitledCatalogue || []

  const { t } = useLocale()

  const catalogueType = applyTo === DiscountApplyToType.SPECIFIC_COLLECTIONS ? 'COLLECTION' : 'PRODUCT_AND_VARIANT'
  const {
    handleItemsSelected,
    handleRemoveGroup,
    handleRemoveIncludedItem,
    handleRemoveExcludedItem,
    handleQuantityChange,
    handleAddEmptyGroup,
    handleVariantUpdate
  } = useEntitledProducts({
    entitlement,
    setFormState,
    catalogueType
  })

  const renderSelectionModeSelector = (
    groupIndex: number,
    realQty: number,
    activeEntitiesCount: number,
    isAll: boolean
  ) => {
    return (
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-1 h-7'>
          <span className='text-text-xs-regular text-zinc-500'>{t('discounts_v2.apply_discount_to')}</span>
          <Select
            value={isAll ? 'all' : 'any'}
            onValueChange={value => {
              if (value === 'all') {
                handleQuantityChange(groupIndex, undefined, EntitledCatalogueSelectionMode.ALL)
              } else {
                handleQuantityChange(groupIndex, '1', EntitledCatalogueSelectionMode.ANY)
              }
            }}
          >
            <SelectTrigger
              id={`selection-mode-trigger-group-${groupIndex}`}
              className='w-fit h-7 border-none bg-transparent gap-1 px-1 focus:ring-0 shadow-none hover:bg-zinc-50 rounded'
            >
              <span className='text-text-xs-medium text-zinc-900'>
                {isAll ? t('discounts_v2.all').toLowerCase() : t('discounts_v2.any').toLowerCase()}
              </span>
            </SelectTrigger>
            <SelectContent className='w-[200px]'>
              <SelectItem id={`selection-mode-all-group-${groupIndex}`} value='all'>
                {t('discounts_v2.all_of_the_products')}
              </SelectItem>
              <SelectItem id={`selection-mode-any-group-${groupIndex}`} value='any'>
                {t('discounts_v2.any_of_x_products')}
              </SelectItem>
            </SelectContent>
          </Select>
          {!isAll && (
            <div className='min-w-10 max-w-fit'>
              <Input
                id={`selection-mode-qty-input-group-${groupIndex}`}
                value={realQty.toString()}
                onChange={e => handleQuantityChange(groupIndex, e.target.value)}
                dimension='sm'
                type='number'
                max={999999}
                variant='filled'
                className={classNames(
                  'min-w-10 text-center px-1',
                  errors[`entitlement.entitledCatalogue.${groupIndex}.quantity`] && 'border-red-500'
                )}
                style={{ width: `${(realQty?.toString().length || 0) + 2}ch` }}
              />
            </div>
          )}
          <span className='text-text-xs-regular text-zinc-500'>{t('discounts_v2.of_the_products')}</span>
        </div>
        <FormErrorText message={errors[`entitlement.entitledCatalogue.${groupIndex}.quantity`]} />
      </div>
    )
  }

  const renderList = () => {
    const displayGroups = items.length > 0 ? items : [[]]
    return (
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col'>
          {displayGroups.map((group, groupIndex) => {
            const isSingleGroup = items.length === 1
            const realQty = entitledCatalogue[groupIndex]?.quantity || 0
            const activeEntitiesCount = group.length
            const selectionMode = entitledCatalogue[groupIndex]?.selectionMode

            const isAll =
              selectionMode === EntitledCatalogueSelectionMode.ALL ||
              (!selectionMode && realQty > 0 && realQty >= activeEntitiesCount)

            return (
              <div key={groupIndex}>
                <div className='rounded-xl flex flex-col '>
                  {(groupIndex > 0 || group.length > 0) && (
                    <div className='flex justify-between items-center pt-3'>
                      <div className='flex items-center gap-2'>
                        {isSingleGroup ? (
                          renderSelectionModeSelector(groupIndex, realQty, activeEntitiesCount, isAll)
                        ) : (
                          <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-2  rounded '>
                              <span className='text-text-xs-regular text-zinc-500 whitespace-nowrap '>
                                {t('discounts_v2.min_qty')}
                              </span>
                              <Input
                                id={`entitled-qty-input-group-${groupIndex}`}
                                variant='filled'
                                placeholder='0'
                                dimension='sm'
                                value={entitledCatalogue[groupIndex]?.quantity?.toString() ?? ''}
                                onChange={e => handleQuantityChange(groupIndex, e.target.value)}
                                type='number'
                                className={classNames(
                                  'min-w-10 text-center px-1',
                                  errors[`entitlement.entitledCatalogue.${groupIndex}.quantity`] && 'border-red-500'
                                )}
                                style={{
                                  width: `${(entitledCatalogue[groupIndex]?.quantity?.toString().length || 1) + 2}ch`
                                }}
                                endAdornment={
                                  <span className='text-text-xs-regular text-zinc-400 '>{t('discounts_v2.qty')}</span>
                                }
                              />
                            </div>
                            <FormErrorText message={errors[`entitlement.entitledCatalogue.${groupIndex}.quantity`]} />
                          </div>
                        )}
                      </div>
                      <div className='flex items-center gap-2'>
                        <span className='text-text-xs-medium text-zinc-500'>
                          {t('discounts_v2.group_count', { count: groupIndex + 1 })}
                        </span>
                        {groupIndex > 0 && (
                          <IconButton
                            id={`remove-entitled-group-button-${groupIndex}`}
                            onClick={() => handleRemoveGroup(groupIndex)}
                            variant='ghost'
                            size='xs'
                            IconComponent={Trash}
                            className='text-zinc-500'
                          />
                        )}
                      </div>
                    </div>
                  )}

                  <CatalogueGroupControl
                    groupIndex={groupIndex}
                    includedGroups={items}
                    excludedGroups={metaData?.excludedCatalogue}
                    catalogueType={catalogueType}
                    actions={{
                      onRemoveIncludedItem: handleRemoveIncludedItem,
                      onRemoveExcludedItem: handleRemoveExcludedItem,
                      onUpdateItem: handleVariantUpdate,
                      onItemsSelected: handleItemsSelected,
                      onAddEmptyGroup: handleAddEmptyGroup
                    }}
                    errorHandling={{
                      errors: discountCatalogueErrors(errors, DiscountFieldPaths.ENTITLED_CATALOGUE)
                    }}
                    config={{
                      showVariantEditor: true,
                      addText: t('discounts_v2.add_product_label'),
                      showExclusionOption: true,
                      emptyText:
                        catalogueType === 'COLLECTION'
                          ? t('discounts_v2.choose_collections')
                          : t('discounts_v2.choose_products')
                    }}
                  />
                </div>
                {groupIndex < items.length - 1 && (
                  <div className='h-[1px] w-full border-t-2 border-dotted border-zinc-500/20 my-2' />
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='flex w-full mb-3'>
      <div className='border-l-1 border-zinc-200 w-5 rounded-l-lg border-y-1 h-auto'></div>
      <div className='flex flex-col gap-2 rounded-lg px-2 w-full '>{renderList()}</div>
      <div className='border-r-1 border-zinc-200 w-5 rounded-r-lg border-y-1  h-auto'></div>
    </div>
  )
}

export default EntitledProductsList
